import { Instance, types } from 'mobx-state-tree';
import { IAccessTemplate, IAdvertisement, IBranch, IBrand, ICategory, IEntityStore, IHarmonization, IHarmonizationRepo, IHarmonizer, IItemCustomization, IMessage, IOrder, IOrderItem, IOutlet, IPretemplate, IPretemplateItem, IReminder, ISupplier, ISupplierItem, ISupplierItemExt, ITemplate, ITemplateItem, IUser } from '../main/entities';
import { mg } from '../mg/mg';
import { AdmAdRepo } from './internal/AdmAdRepo';
import { AdmUserRepo } from './internal/AdmUserRepo';
import { HarmonizationRepo } from './internal/HarmonizationRepo';
import { SortedRepo } from './internal/SortedRepo';
import { UnionRepo } from './internal/UnionRepo';
import { getOmnistore, IOmnistore } from './Omnistore';

export const Multirepo = types
	.model('Multirepo', {
	})
	.views(self => ({
		get omnistore() : IOmnistore {
			return getOmnistore();
		},
	}))
	.views(self => ({
		get entityManagerStore() {
			return self.omnistore.entityManagerStore;
		},
		get locationStore() {
			return self.omnistore.locationStore;
		},
	}))
	.views(self => ({
		get userRepo() {
			return self.entityManagerStore.get('User') as IEntityStore<IUser>;
		},
		get branchRepo() {
			return new SortedRepo(self.entityManagerStore.get('Branch') as IEntityStore<IBranch>, mg.comparatorOn(b => b.name));
		},
		get outletRepo() {
			return self.entityManagerStore.get('Outlet') as IEntityStore<IOutlet>;
		},
		get supplierRepo() {
			return self.entityManagerStore.get('Supplier') as IEntityStore<ISupplier>;
		},
		get admSupplierRepo() {
			return self.entityManagerStore.get('AdminSupplier') as IEntityStore<ISupplier>;
		},
		get templateRepo() {
			return self.entityManagerStore.get('Template') as IEntityStore<ITemplate>;
		},
		get orderRepo() {
			return self.entityManagerStore.get('Order') as IEntityStore<IOrder>;
		},
		get advertisementRepo() {
			return self.entityManagerStore.get('Advertisement') as IEntityStore<IAdvertisement>;
		},
		get messageRepo() {
			return self.entityManagerStore.get('Message') as IEntityStore<IMessage>;
		},
		get reminderRepo() {
			return self.entityManagerStore.get('Reminder') as IEntityStore<IReminder>;
		},
		get accessTemplateRepo() {
			return self.entityManagerStore.get('AccessTemplate') as IEntityStore<IAccessTemplate>;
		},
		get publicSupplierRepo() {
			return self.entityManagerStore.get('PublicSupplier') as IEntityStore<ISupplier>;
		},
		get itemCustomizationRepo() {
			return self.entityManagerStore.get('ItemCustomization') as IEntityStore<IItemCustomization>;
		},
		get masterSupplierRepo() {
			return self.entityManagerStore.get('MasterSupplier') as IEntityStore<ISupplier>;
		},
		get brandRepo() {
			return self.entityManagerStore.get('Brand') as IEntityStore<IBrand>;
		},
		get harmonizationRepo() {
			const delegate = self.entityManagerStore.get('Harmonization') as IEntityStore<IHarmonization>;
			return new HarmonizationRepo(delegate) as IHarmonizationRepo;
		},
		get harmonizerRepo() {
			return self.entityManagerStore.get('Harmonizer') as IEntityStore<IHarmonizer>;
		},
	}))
	.views(self => ({
		get admUserRepo() {
			return new AdmUserRepo(self.userRepo, self.entityManagerStore.get('UserStat'));
		},
		get registeredSupplierRepo() {
			return UnionRepo.newRepo('registeredSupplierRepo', self.masterSupplierRepo, self.publicSupplierRepo);
		},
		get admAdRepo() {
			const delegate = (self.entityManagerStore.get('AdmAd') as IEntityStore<IAdvertisement>);
			return new AdmAdRepo(delegate, self.masterSupplierRepo);
		},
		admPretemplateRepo(id: number) {
			return self.entityManagerStore.get('Pretemplate', id) as IEntityStore<IPretemplate>;
		},
		admPretemplateItemRepo(id: number) {
			return self.entityManagerStore.get('PretemplateItem', id) as IEntityStore<IPretemplateItem>;
		},
		supplierItemRepo(id: number) {
			return self.entityManagerStore.get('SupplierItem', id) as IEntityStore<ISupplierItem>;
		},
		supplierItemExtRepo(id: number) {
			return self.entityManagerStore.get('SupplierItemExt', id) as IEntityStore<ISupplierItemExt>;
		},
		specialOfferRepo(id: number) {
			return self.entityManagerStore.get('SpecialOffer', id) as IEntityStore<ISupplierItem>;
		},
		templateItemRepo(id: number) {
			return self.entityManagerStore.get('TemplateItem', id) as IEntityStore<ITemplateItem>;
		},
		orderItemRepo(id: number) {
			return self.entityManagerStore.get('OrderItem', id) as IEntityStore<IOrderItem>;
		},
		categoryRepo(branchId: number) {
			return self.entityManagerStore.get('Category', branchId) as IEntityStore<ICategory>;
		},
	}))
	.views(self => ({
		get allSupplierRepo() {//TODO replace by admSupplierRepo
			return UnionRepo.newRepo('allSupplierRepo', self.supplierRepo, self.registeredSupplierRepo);
		},
	}))
	.views(self => ({
		get currentBranch() {
			return self.branchRepo.byId(self.locationStore.branchId);
		},
		get currentSupplier() {
			return self.supplierRepo.byId(self.locationStore.supplierId);
		},
		get currentTemplate() : ITemplate | undefined {
			const x = self.locationStore;
			return self.templateRepo.allAsList.filter(t => t.branchId===x.branchId && t.supplierId===x.supplierId)[0];
		},
		get _currentTemplates() {
			const x = self.locationStore;
			const f = (t: ITemplate) => (t.branchId===x.branchId || !x.branchId)
			&& (t.supplierId===x.supplierId || !x.supplierId);
			return self.templateRepo.allAsList.filter(f);
		},
		get _currentCategoryRepo() {
			return self.categoryRepo(self.locationStore.branchId);
		},
	}))
	.views(self => ({
		get _currentSuppliers() {
			return self._currentTemplates.map(t => t.supplierId).map(self.supplierRepo.byId).filter(mg.isPresent);
		},
	}))
	.actions(self => ({
		fetchCurrentCategories() {
			self._currentCategoryRepo.fetch();
		},
	}))
	.views(self => ({
		get currentSuppliers() {
			return self._currentSuppliers.slice().sort(mg.comparatorOn((s: ISupplier) => s.name));
		},
		get currentTemplates() {
			const sn = (supplierId: number) => {
				const s = self.supplierRepo.byId(supplierId);
				return s ? s.name : '?';
			};
			const templateIdToSupplierName = new Map<number, string>(self.templateRepo.allAsList.map(t => [t.id, sn(t.supplierId)]));
			return self._currentTemplates.slice().sort(mg.comparatorOn((t: ITemplate) => templateIdToSupplierName.get(t.id)));
		},
		get currentCategories() {
			return self._currentCategoryRepo.allAsList.map((e: ICategory) => e.name);
		},
	}))
	.views(self => ({
		_externalGetEntity(head: string, id: number) {
			switch (head) {
			case 'branch': return self.branchRepo.byId(id);
			case 'template': return self.templateRepo.byId(id);
			case 'supplier': return self.allSupplierRepo.byId(id);
			case 'owner': return self.userRepo.byId(id);
			}
		},
	}))
	.views(self => ({
		externalGet(entity: any, fieldName: string, dotIndex?: number) {
			if (dotIndex === undefined) dotIndex = fieldName.indexOf('.');
			while (dotIndex != -1) {
				mg.assertTrue(dotIndex < fieldName.length-1, fieldName, dotIndex);
				mg.assertTrue(fieldName[dotIndex] === '.', fieldName, dotIndex);
				const head = fieldName.substring(0, dotIndex);
				const tail = fieldName.substring(dotIndex + 1);
				let id = entity[head + 'Id'];
				// if (typeof id !== 'number') id = entity[head]; //TODO need it?
				if (typeof id !== 'number') return undefined;
				if (!id) return undefined;
				const e = self._externalGetEntity(head, id);
				if (e === undefined) return undefined;
				if (!mg.assertTrue(typeof e === 'object')) return undefined;

				entity = e;
				dotIndex = tail.indexOf('.');
				fieldName = tail;
			}
			return entity[fieldName];
		}
	}));

export type IMultirepo = Instance<typeof Multirepo>
